import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export type FeatureSource = 'plan' | 'override' | 'attestation' | 'none'

export interface TenantFeature {
  key: string
  name: string
  description: string
  category: string
  has_access: boolean
  source: FeatureSource
  expires_at?: string | null
}

export interface TenantFeatureOverride {
  feature_key: string
  feature_name: string
  granted_by_id: string
  granted_by_name: string
  granted_at: string
  expires_at: string | null
  is_active: boolean
}

export interface GrantFeatureRequest {
  expires_in_days?: number | null // null = permanent, number = temporary
}

interface UseTenantFeaturesOptions {
  category?: string
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  tenantFeatures: (tenantId: string, options: UseTenantFeaturesOptions) =>
    ['tenant-features', tenantId, options],
  tenantOverrides: (tenantId: string) =>
    ['tenant-feature-overrides', tenantId],
}

// =============================================================================
// TENANT FEATURE QUERIES
// =============================================================================

/**
 * Fetch all features with tenant access status
 */
export function useTenantFeatures(tenantId: string | null, options: UseTenantFeaturesOptions = {}) {
  const { category } = options

  return useQuery<TenantFeature[]>({
    queryKey: QUERY_KEYS.tenantFeatures(tenantId || '', { category }),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (category) params.append('category', category)

      const res = await api.get(`/admin/tenants/${tenantId}/features?${params.toString()}`)
      return res.data
    },
    enabled: !!tenantId,
    staleTime: 1000 * 60, // 1 minute - features can change with plan updates
  })
}

/**
 * Fetch all feature overrides for a tenant
 */
export function useTenantFeatureOverrides(tenantId: string | null) {
  return useQuery<TenantFeatureOverride[]>({
    queryKey: QUERY_KEYS.tenantOverrides(tenantId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/tenants/${tenantId}/features/overrides`)
      return res.data
    },
    enabled: !!tenantId,
  })
}

// =============================================================================
// TENANT FEATURE MUTATIONS
// =============================================================================

interface GrantTenantFeatureParams {
  tenantId: string
  featureKey: string
  expiresInDays?: number | null
}

/**
 * Grant a feature to a tenant (permanent or temporary)
 */
export function useGrantTenantFeature() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, GrantTenantFeatureParams>({
    mutationFn: async ({ tenantId, featureKey, expiresInDays }) => {
      const data: GrantFeatureRequest = {}
      if (expiresInDays !== undefined) {
        data.expires_in_days = expiresInDays
      }

      await api.post(`/admin/tenants/${tenantId}/features/${featureKey}/grant`, data)
    },
    onSuccess: (_, variables) => {
      // Invalidate tenant feature queries
      queryClient.invalidateQueries({
        queryKey: ['tenant-features', variables.tenantId]
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.tenantOverrides(variables.tenantId)
      })
    },
  })
}

interface RevokeTenantFeatureParams {
  tenantId: string
  featureKey: string
}

/**
 * Revoke a feature override from a tenant
 */
export function useRevokeTenantFeature() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, RevokeTenantFeatureParams>({
    mutationFn: async ({ tenantId, featureKey }) => {
      await api.delete(`/admin/tenants/${tenantId}/features/${featureKey}/revoke`)
    },
    onSuccess: (_, variables) => {
      // Invalidate tenant feature queries
      queryClient.invalidateQueries({
        queryKey: ['tenant-features', variables.tenantId]
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.tenantOverrides(variables.tenantId)
      })
    },
  })
}

/**
 * Sync tenant features based on current plan
 */
export function useSyncTenantFeatures() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (tenantId) => {
      await api.post(`/admin/tenants/${tenantId}/features/sync`)
    },
    onSuccess: (_, tenantId) => {
      // Invalidate tenant feature queries
      queryClient.invalidateQueries({
        queryKey: ['tenant-features', tenantId]
      })
    },
  })
}
